import { forwardRef, useImperativeHandle } from 'react';
import { translateApiError } from 'i18n/translateApiError';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Progress,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiCheckCircle, FiFileText, FiUploadCloud, FiX } from 'react-icons/fi';
import API from 'api/api';
import { useTranslation } from 'i18n/TranslationProvider';

const EXTENSION_TO_MIME = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const statusProgress = {
  idle: 0,
  requesting: 25,
  uploading: 70,
  confirming: 90,
  success: 100,
};

const formatFileSize = (size = 0) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIdentity = (file) =>
  `${file?.name || ''}:${file?.size || 0}:${file?.lastModified || 0}`;

const mergeUniqueFiles = (existingFiles, incomingFiles) => {
  const map = new Map();
  [...(existingFiles || []), ...(incomingFiles || [])].forEach((file) => {
    if (!file) return;
    map.set(fileIdentity(file), file);
  });
  return Array.from(map.values());
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function DocumentUploader({
  entityType,
  entityId,
  onUploaded,
  ensureEntityId,
  isDisabled = false,
  accept = '.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx',
  docType = 'general',
  multiple = false,
  isInvoice = false,
  hideUploadButton = false,
  onFilesChange,
}, ref) {
  const { t } = useTranslation();
  const statusLabels = {
    idle: t('auth.ready_to_upload'),
    requesting: t('auth.requesting_upload_url'),
    uploading: t('auth.uploading_to_storage'),
    confirming: t('auth.confirming_upload'),
    success: t('auth.upload_completed'),
  };

  const [files, setFiles] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const panelBg = useColorModeValue('white', 'whiteAlpha.60');
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const dropBg = useColorModeValue('blue.50', 'whiteAlpha.120');
  const dropBorder = useColorModeValue('blue.200', 'blue.300');
  const dropActiveBg = useColorModeValue('blue.100', 'whiteAlpha.180');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const strongText = useColorModeValue('navy.700', 'white');
  const fileCardBg = useColorModeValue('white', 'whiteAlpha.100');

  const resetState = useCallback(() => {
    setFiles([]);
    setInvoiceNumber('');
    setStatus('idle');
    onFilesChange?.([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onFilesChange]);

  const getFileType = useCallback((file) => {
    if (file?.type) return file.type;
    const extension = file?.name?.split('.').pop()?.toLowerCase();
    return EXTENSION_TO_MIME[extension] || 'application/octet-stream';
  }, []);

  const setSelectedFiles = useCallback((incomingFiles, append = false) => {
    const normalized = multiple ? incomingFiles : incomingFiles.slice(0, 1);
    setFiles((previous) => {
      const next = multiple && append
        ? mergeUniqueFiles(previous, normalized)
        : normalized;
      onFilesChange?.(next);
      return next;
    });
    setError(null);
  }, [multiple, onFilesChange]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setSelectedFiles(selectedFiles, multiple);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFileAt = (index) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      onFilesChange?.(next);
      return next;
    });
  };

  const openFilePicker = () => {
    if (isDisabled || isBusy) return;
    fileInputRef.current?.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (isDisabled || isBusy) return;
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    setSelectedFiles(droppedFiles, multiple);
  };

  const uploadSingleFile = useCallback(async (file, resolvedEntityId) => {
    const endpoint = isInvoice ? '/invoices/upload' : '/documents/request-upload';
    const fileType = getFileType(file);

    const payload = {
      fileName: file.name,
      fileType,
      fileSize: file.size,
      entityType,
      entityId: resolvedEntityId,
      docType,
    };

    if (isInvoice) {
      payload.invoiceNumber = invoiceNumber;
    }

    setStatus('requesting');
    const requestResponse = await API.post(endpoint, payload);
    const { documentId, uploadUrl, invoiceId } = requestResponse.data.data;
    const finalId = isInvoice ? invoiceId : documentId;

    setStatus('uploading');
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': fileType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Storage upload failed with status ${uploadResponse.status}`);
    }

    if (!isInvoice) {
      setStatus('confirming');
      let lastError = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await API.post(`/documents/${finalId}/confirm`);
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
          if (attempt < 2) {
            await sleep(250 * (attempt + 1));
          }
        }
      }
      if (lastError) {
        throw lastError;
      }
    }

    return {
      id: finalId,
      fileName: file.name,
    };
  }, [isInvoice, invoiceNumber, entityType, docType, getFileType]);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      setError(t('Select a file before uploading.'));
      return {
        success: false,
        reason: 'no_files',
      };
    }

    if (isInvoice && !invoiceNumber) {
      setError(t('Please enter an invoice number.'));
      return {
        success: false,
        reason: 'missing_invoice_number',
      };
    }

    if (isInvoice && !entityId) {
      setError(t('Please select an order before uploading an invoice.'));
      return {
        success: false,
        reason: 'missing_invoice_order',
      };
    }

    try {
      setError(null);
      let resolvedEntityId = entityId;

      if (!resolvedEntityId && !isInvoice) {
        if (typeof ensureEntityId !== 'function') {
          setError(t('Please save the record before uploading documents.'));
          return {
            success: false,
            reason: 'missing_entity',
          };
        }
        resolvedEntityId = await ensureEntityId();
        if (!resolvedEntityId) {
          setError(t('Unable to prepare the record for upload.'));
          return {
            success: false,
            reason: 'entity_prepare_failed',
          };
        }
      }

      const results = [];
      for (const file of files) {
        const result = await uploadSingleFile(file, resolvedEntityId);
        results.push(result);
      }

      setStatus('success');
      toast({
        title: t('Upload successful'),
        description: t('{{count}} file(s) uploaded successfully.', {
          count: files.length,
        }),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      onUploaded?.(results, { entityId: resolvedEntityId });

      setTimeout(() => {
        resetState();
      }, 1200);

      return {
        success: true,
        results,
        entityId: resolvedEntityId,
      };
    } catch (err) {
      console.error('Document upload failed:', err);
      setStatus('idle');
      setError(translateApiError(err, t('Upload failed')));
      toast({
        title: t('Upload failed'),
        description: translateApiError(err, t('Unable to upload the document.')),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return {
        success: false,
        error: err,
      };
    }
  }, [
    files,
    isInvoice,
    invoiceNumber,
    entityId,
    ensureEntityId,
    uploadSingleFile,
    onUploaded,
    t,
    toast,
    resetState,
  ]);

  const isBusy = status !== 'idle' && status !== 'success';
  const statusLabel = statusLabels[status] || statusLabels.idle;
  const progressValue = statusProgress[status] ?? 0;
  const acceptedText = useMemo(() => {
    return accept
      .split(',')
      .map((item) => item.trim().replace('.', '').toUpperCase())
      .join(', ');
  }, [accept]);

  useImperativeHandle(
    ref,
    () => ({
      upload: handleUpload,
      hasFiles: () => files.length > 0,
      isBusy: () => isBusy,
    }),
    [files.length, isBusy, handleUpload],
  );

  return (
    <Box mt={2}>
      <Stack spacing={4}>
        {isInvoice && (
          <FormControl isRequired>
            <FormLabel>{t('Invoice Number')}</FormLabel>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="INV-..."
              isDisabled={isDisabled || isBusy}
            />
          </FormControl>
        )}

        <Input
          data-testid="document-file-input"
          type="file"
          accept={accept}
          onChange={handleFileChange}
          isDisabled={isDisabled || isBusy}
          multiple={multiple}
          ref={fileInputRef}
          display="none"
        />

        <Box
          bg={panelBg}
          border="1px solid"
          borderColor={panelBorder}
          borderRadius="18px"
          p={{ base: 4, md: 5 }}
        >
          <Box
            border="1.5px dashed"
            borderColor={isDragging ? 'blue.400' : dropBorder}
            bg={isDragging ? dropActiveBg : dropBg}
            borderRadius="16px"
            p={{ base: 5, md: 8 }}
            textAlign="center"
            transition="all 0.18s ease"
            cursor={isDisabled || isBusy ? 'not-allowed' : 'pointer'}
            opacity={isDisabled ? 0.65 : 1}
            onClick={openFilePicker}
            onDragOver={(event) => {
              event.preventDefault();
              if (!isDisabled && !isBusy) setIsDragging(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            role="button"
            tabIndex={isDisabled || isBusy ? -1 : 0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openFilePicker();
              }
            }}
          >
            <Stack spacing={3} align="center">
              <Icon as={FiUploadCloud} boxSize={{ base: 7, md: 9 }} color="blue.400" />
              <Text fontWeight="700" color={strongText} fontSize={{ base: 'sm', md: 'md' }}>
                {t('auth.drag_and_drop_files_here')}
              </Text>
              <Text color={mutedText} fontSize="sm">
                {t('or')}
              </Text>
              <Button
                type="button"
                size={{ base: 'sm', md: 'md' }}
                onClick={(event) => {
                  event.stopPropagation();
                  openFilePicker();
                }}
                isDisabled={isDisabled || isBusy}
                colorScheme="blue"
                variant="outline"
              >
                {t(multiple ? 'auth.choose_files' : 'auth.choose_file')}
              </Button>
              <Text color={mutedText} fontSize="xs">
                {t('auth.supported_formats_max_size', { formats: acceptedText })}
              </Text>
            </Stack>
          </Box>

          {files.length > 0 && (
            <Box mt={4}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="700" color={strongText}>
                  {t('auth.selected_files')}
                </Text>
                <Badge colorScheme="blue" borderRadius="full" px={2}>
                  {files.length}
                </Badge>
              </HStack>
              <Stack spacing={2}>
                {files.map((file, index) => (
                  <HStack
                    key={`${file.name}-${index}`}
                    justify="space-between"
                    border="1px solid"
                    borderColor={panelBorder}
                    borderRadius="12px"
                    px={3}
                    py={2}
                    bg={fileCardBg}
                  >
                    <HStack minW={0} spacing={2}>
                      <Icon as={FiFileText} color="blue.400" boxSize={4} />
                      <Box minW={0}>
                        <Text noOfLines={1} fontWeight="600" fontSize="sm" color={strongText}>
                          {file.name}
                        </Text>
                        <Text fontSize="xs" color={mutedText}>
                          {formatFileSize(file.size)}
                        </Text>
                      </Box>
                    </HStack>
                    <IconButton
                      type="button"
                      aria-label={t('Remove file')}
                      icon={<FiX />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeFileAt(index)}
                      isDisabled={isBusy}
                    />
                  </HStack>
                ))}
              </Stack>
            </Box>
          )}

          {(isBusy || status === 'success') && (
            <Box mt={4}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" color={mutedText}>
                  {statusLabel}
                </Text>
                <Text fontSize="xs" color={mutedText}>
                  {progressValue}%
                </Text>
              </HStack>
              <Progress
                value={progressValue}
                size="sm"
                borderRadius="full"
                colorScheme={status === 'success' ? 'green' : 'blue'}
                hasStripe={status !== 'success'}
                isAnimated={status !== 'success'}
              />
            </Box>
          )}
        </Box>

        {!hideUploadButton && (
          <Button
            onClick={handleUpload}
            isDisabled={isDisabled || files.length === 0 || isBusy}
            isLoading={isBusy}
            loadingText={statusLabel}
            colorScheme="blue"
            leftIcon={status === 'success' ? <FiCheckCircle /> : undefined}
            w={{ base: '100%', sm: 'fit-content' }}
          >
            {t('auth.upload')}
          </Button>
        )}

        {!entityId && !isInvoice && typeof ensureEntityId !== 'function' && (
          <Text fontSize="xs" color="gray.500">
            {t('auth.save_record_before_upload')}
          </Text>
        )}
        <Text fontSize="xs" color={mutedText}>
          {t('auth.files_stored_securely')}
        </Text>
        {error && (
          <Text fontSize="xs" color="red.500">
            {error}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

export default forwardRef(DocumentUploader);
